import { Controller } from "@hotwired/stimulus";
import "./component.scss";
import { MOBILE_QUERY } from "../../../helpers/breakpoints";

// Primary brand color, see .claude/DESIGN.md (primary/40)
const LINE_COLOR = "#4e38f8";

// Neutral reference grays, see ../tokens.scss
const GRAY_LINE = "#d2d2d6";
const AXIS_COLOR = "#bababa";
const GRID_COLOR = "rgba(32, 32, 32, 0.08)";
// Inactive bars of the mobile sales chart ($dash-bar-light in ../tokens.scss)
const BAR_COLOR = "#e3defb";

// Dashed vertical crosshair through the hovered point (Figma reference look).
const crosshairPlugin = {
  id: "affiliateDashboardCrosshair",
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements();
    if (!active?.length) return;

    const { ctx, chartArea } = chart;
    const x = active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = AXIS_COLOR;
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

const DEFAULT_METRIC = "gross_revenue";

export default class extends Controller {
  static targets = [
    "canvas",
    "title",
    "total",
    "deltaValue",
    "empty",
    "wrapper",
    "summary",
  ];
  static values = {
    labels: Array,
    points: Array,
    previous: Array,
    previousLabels: Array,
    metrics: Object,
    currency: String,
    locale: String,
    maxXTicks: { type: Number, default: 8 },
  };

  // Chart.js is imported dynamically so the chunk stays out of the main bundle and
  // loads regardless of how the page was reached (full load or turbo frame visit) —
  // the analytics_charts entrypoint pattern breaks inside frames because layout-less
  // frame responses never run content_for :head.
  async connect() {
    if (!this.hasCanvasTarget) return;

    // Turbo Drive shows a cached snapshot (preview) before the real response
    // arrives. Both renders call connect(), which would animate the chart twice.
    // Skip animation on the preview — the real render animates normally.
    this.isPreview = document.documentElement.hasAttribute("data-turbo-preview");

    // Invalidated by disconnect(): a reconnect (or Turbo cache restore) must not
    // be finished by the import of an earlier, already discarded connect.
    this.generation = (this.generation || 0) + 1;
    const generation = this.generation;

    this.media = window.matchMedia(MOBILE_QUERY);
    this.onBreakpointChange = () => this.applyBreakpoint();
    this.media.addEventListener("change", this.onBreakpointChange);

    // Mobile (Figma 7:248): borderless sparkline-style chart — no axes, no
    // grid, thinner line, a dot on the last point, no comparison line.
    this.mobile = this.media.matches;
    this.requestedMetric = DEFAULT_METRIC;
    // Bound BEFORE the import: the stat tiles are tappable as soon as the page is
    // interactive, and a tap during a slow import must not be swallowed — it is
    // recorded as the requested metric and replayed once Chart.js has landed.
    this.bindMetricEvents();

    const {
      Chart,
      LineElement,
      PointElement,
      LineController,
      BarElement,
      BarController,
      CategoryScale,
      LinearScale,
      Tooltip,
    } = await import("chart.js");

    if (generation !== this.generation || !this.hasCanvasTarget) return;

    Chart.register(
      LineElement,
      PointElement,
      LineController,
      BarElement,
      BarController,
      CategoryScale,
      LinearScale,
      Tooltip
    );

    this.Chart = Chart;
    // The breakpoint may have changed while the import was in flight.
    this.mobile = this.media.matches;
    this.bindMetricEvents();
    // Always applied, never skipped: header texts and the empty-state text may
    // still carry the mutations of a previous visit (Turbo page cache), and the
    // chart itself has to be rebuilt from the server-rendered metric.
    this.applyMetric(this.requestedMetric);
  }

  disconnect() {
    this.generation = (this.generation || 0) + 1;
    this.media?.removeEventListener("change", this.onBreakpointChange);
    this.media = undefined;
    this.unbindMetricEvents();
    this.popup?.remove();
    this.popup = undefined;
    this.chart?.destroy();
    this.chart = undefined;
    this.Chart = undefined;
  }

  // Rotating a phone or resizing a window crosses the breakpoint without a
  // reconnect — rebuild for the other layout and start from the default metric.
  //
  // Deliberately without a "did anything change?" guard: a change event only
  // fires on a real flip, and the guard would skip the reset in the one ordering
  // that needs it most — a finishing import re-reads the new breakpoint itself
  // and would otherwise leave the chart on the metric that was tapped before the
  // switch, while the tiles have already fallen back to the default.
  applyBreakpoint() {
    this.mobile = this.media.matches;
    this.bindMetricEvents();
    this.applyMetric(DEFAULT_METRIC);
  }

  // Stat tiles act as chart selectors on mobile (page-level kpis controller).
  bindMetricEvents() {
    const wanted = this.mobile && Object.keys(this.metricsValue).length > 0;
    if (wanted === Boolean(this.onMetricSelect)) return;

    if (!wanted) return this.unbindMetricEvents();

    this.onMetricSelect = (event) => this.showMetric(event.detail.key);
    window.addEventListener(
      "affiliate-dashboard-kpis:select",
      this.onMetricSelect
    );
  }

  unbindMetricEvents() {
    if (!this.onMetricSelect) return;

    window.removeEventListener(
      "affiliate-dashboard-kpis:select",
      this.onMetricSelect
    );
    this.onMetricSelect = undefined;
  }

  // Falls back to the server-rendered gross series when no metrics payload is
  // present (metrics option not passed).
  currentMetric() {
    const metric = this.metricsValue[this.metricKey];
    if (metric) return metric;

    return {
      type: "line",
      values: this.pointsValue,
      previous:
        this.previousValue.length > 0
          ? { values: this.previousValue, labels: this.previousLabelsValue }
          : null,
      empty: this.pointsValue.every((value) => value === 0),
    };
  }

  showMetric(key) {
    if (key === this.requestedMetric || !this.metricsValue[key]) return;

    this.applyMetric(key);
  }

  applyMetric(key) {
    this.requestedMetric = this.metricsValue[key] ? key : DEFAULT_METRIC;
    // Chart.js not imported yet — connect() replays the requested metric.
    if (!this.Chart) return;

    this.metricKey = this.requestedMetric;
    const metric = this.currentMetric();

    if (this.hasTitleTarget && metric.title) {
      this.titleTarget.textContent = metric.title;
    }
    if (this.hasTotalTarget && metric.total) {
      this.totalTarget.textContent = metric.total;
    }
    if (this.hasDeltaValueTarget && metric.delta) {
      this.deltaValueTarget.textContent = `${metric.delta.negative ? "▼" : "▲"} ${metric.delta.text}`;
    }
    // The canvas is aria-hidden, so this text IS the chart for anyone who
    // cannot see it — it has to follow the switch like the visible header does.
    // Server-formatted; no money is assembled here (money.md).
    if (this.hasSummaryTarget && metric.summary) {
      this.summaryTarget.textContent = metric.summary;
    }

    this.chart?.destroy();
    this.chart = undefined;
    this.selectedBar = null;
    this.hidePopup();

    // Only the screen reader text switches; the canvas stays and is drawn as a
    // flat gray line for a period without data.
    if (this.hasEmptyTarget) this.emptyTarget.hidden = !metric.empty;
    this.renderChart();
  }

  renderChart() {
    const metric = this.currentMetric();

    // Zero bars have no height, so an empty sales chart would show nothing at
    // all — it falls back to the same flat line as the other metrics.
    if (this.mobile && metric.type === "bar" && !metric.empty) {
      this.renderBarChart(metric);
    } else {
      this.renderLineChart(metric);
    }
  }

  renderLineChart(metric) {
    const mobile = this.mobile;
    const values = metric.values;
    // Without data the same chart is drawn in gray along the zero baseline. No
    // end dot and no comparison line, so there is nothing to read into it.
    const empty = metric.empty;
    const lineColor = empty ? GRAY_LINE : LINE_COLOR;
    // Chart.js draws nothing through a single point, and an empty period can
    // collapse to one bin — "Alle" has no history to span. Stretching it across
    // two zeroes is what makes the flat line appear. The one label stays on the
    // right where the last one sits in every other range, so the axis keeps both
    // its height and its meaning.
    const flat = empty && values.length < 2;
    const points = flat ? [0, 0] : values;
    const labels = flat ? [ "", this.labelsValue[0] ?? "" ] : this.labelsValue;
    // One real bin is a measurement, but it cannot form a line segment. Keep it
    // honest as one visible point instead of inventing a second date/value.
    const single = !empty && points.length === 1;

    const pointStyle = (color) => ({
      pointRadius: 0,
      pointHitRadius: empty ? 0 : 12,
      pointHoverRadius: empty ? 0 : 5,
      pointHoverBackgroundColor: "#ffffff",
      pointHoverBorderColor: color,
      pointHoverBorderWidth: 2,
    });

    const lastIndex = points.length - 1;
    const datasets = [
      {
        data: points,
        borderColor: lineColor,
        borderWidth: mobile ? 2 : 3,
        fill: false,
        tension: 0.4,
        ...pointStyle(lineColor),
        pointRadius:
          single
            ? 4
            : mobile && !empty
            ? (ctx) => (ctx.dataIndex === lastIndex ? 4 : 0)
            : 0,
        pointBackgroundColor: lineColor,
        pointBorderColor: lineColor,
      },
    ];

    if (!mobile && !empty && this.previousValue.length > 0) {
      datasets.push({
        data: this.previousValue,
        borderColor: GRAY_LINE,
        borderWidth: 2.5,
        fill: false,
        tension: 0.4,
        ...pointStyle(GRAY_LINE),
      });
    }

    this.chart = new this.Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels,
        datasets,
      },
      // The empty line is orientation only, not a measured series. Without the
      // plugin there is no crosshair pointing at its synthetic zeroes.
      plugins: empty ? [] : [crosshairPlugin],
      options: {
        // `events` omitted means Chart.js defaults for real data. An empty list
        // makes the decorative line completely inert on mouse and touch.
        ...(empty ? { events: [] } : {}),
        animation: this.isPreview ? false : undefined,
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: ({ tooltip }) => {
              if (tooltip.opacity === 0 || tooltip.dataPoints?.length === 0) {
                this.hidePopup();
                return;
              }

              this.showPopup(tooltip.dataPoints[0].dataIndex, {
                x: tooltip.caretX,
                y: tooltip.caretY,
              });
            },
          },
        },
        scales: {
          x: {
            display: !mobile,
            grid: { display: false },
            border: { display: false },
            ticks: {
              maxTicksLimit: this.maxXTicksValue,
              maxRotation: 0,
              color: AXIS_COLOR,
              font: { size: 11 },
              // Keeps the first and last label inside the plot area instead of
              // centring them under their point. Centred, a wide edge label
              // ("23. Jul.") makes Chart.js indent the whole area, so the line
              // started at a different x in every range.
              align: "inner",
            },
          },
          y: {
            display: !mobile,
            beginAtZero: true,
            border: { display: false, dash: [4, 4] },
            // Gridlines while empty would be drawn over an invented range and
            // read as data; the axis stays only to reserve its width.
            grid: { color: GRID_COLOR, display: !empty },
            ticks: {
              maxTicksLimit: 5,
              color: AXIS_COLOR,
              font: { size: 11 },
              // Empty: label the zero and leave the invented steps above it
              // blank, so the axis keeps the width it has when there is data.
              callback: (value) =>
                empty && value !== 0
                  ? ""
                  : this.formatValue(value, { maximumFractionDigits: 0 }),
            },
          },
        },
      },
    });
  }

  // Mobile sales chart (reference design): light bars, the selected bar in full
  // primary; tapping a bar selects it and opens the comparison popup.
  renderBarChart(metric) {
    this.selectedBar = metric.values.length - 1;

    this.chart = new this.Chart(this.canvasTarget, {
      type: "bar",
      data: {
        labels: this.labelsValue,
        datasets: [
          {
            data: metric.values,
            backgroundColor: (ctx) =>
              ctx.dataIndex === this.selectedBar ? LINE_COLOR : BAR_COLOR,
            hoverBackgroundColor: (ctx) =>
              ctx.dataIndex === this.selectedBar ? LINE_COLOR : BAR_COLOR,
            borderRadius: 3,
            borderSkipped: false,
            barPercentage: 0.72,
            categoryPercentage: 0.9,
          },
        ],
      },
      options: {
        animation: this.isPreview ? false : undefined,
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        onClick: (_event, elements, chart) => {
          if (!elements.length) return;

          this.selectedBar = elements[0].index;
          chart.update("none");
          const bar = chart.getDatasetMeta(0).data[elements[0].index];
          this.showPopup(elements[0].index, { x: bar.x, y: bar.y });
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        scales: {
          x: { display: false, grid: { display: false } },
          y: { display: false, beginAtZero: true },
        },
      },
    });
  }

  // Small comparison popup above the selected bar ("• 20. Juni 12 / • 20. Mai 9 / +33%").
  showPopup(index, anchor) {
    const metric = this.currentMetric();
    const value = metric.values[index];
    const label = this.labelsValue[index];
    const prevValue = metric.previous?.values?.[index];
    const prevLabel = metric.previous?.labels?.[index];
    const percent = this.percentChange(prevValue, value);

    if (value === undefined || label === undefined) {
      this.hidePopup();
      return;
    }

    const chip =
      percent === null
        ? ""
        : `<span class="affiliate-dashboard__popup-chip${percent < 0 ? " affiliate-dashboard__popup-chip--down" : ""}">${percent > 0 ? "+" : ""}${percent}%</span>`;
    const previousRow =
      prevLabel == null || prevValue == null
        ? ""
        : `<div class="affiliate-dashboard__popup-row">
             <span class="affiliate-dashboard__popup-dot affiliate-dashboard__popup-dot--previous"></span>
             <span class="affiliate-dashboard__popup-label">${prevLabel}</span>
             <span class="affiliate-dashboard__popup-value affiliate-dashboard__popup-value--muted">${this.formatValue(prevValue)}</span>
           </div>`;

    const popup = this.popupElement();
    popup.innerHTML = `<div class="affiliate-dashboard__popup-row">
        <span class="affiliate-dashboard__popup-dot affiliate-dashboard__popup-dot--current"></span>
        <span class="affiliate-dashboard__popup-label">${label}</span>
        <span class="affiliate-dashboard__popup-value">${this.formatValue(value)}</span>
        ${chip}
      </div>${previousRow}`;
    popup.hidden = false;

    const maxLeft = this.wrapperTarget.clientWidth - popup.offsetWidth;
    const left = Math.max(0, Math.min(anchor.x - popup.offsetWidth / 2, maxLeft));
    popup.style.left = `${left}px`;
    popup.style.top = `${Math.max(anchor.y - popup.offsetHeight - 8, 0)}px`;
  }

  popupElement() {
    if (!this.popup) {
      this.popup = document.createElement("div");
      this.popup.className = "affiliate-dashboard__chart-popup";
      this.popup.hidden = true;
      this.wrapperTarget.appendChild(this.popup);
    }

    return this.popup;
  }

  hidePopup() {
    if (this.popup) this.popup.hidden = true;
  }

  // Axis ticks and tooltips are the only numbers JS produces itself. Currency and
  // locale come from the server and are never guessed here (money.md): without a
  // currency the value is rendered as a plain number, and an empty locale falls
  // back to the runtime default rather than to a hardcoded one.
  formatValue(value, options = {}) {
    const locale = this.localeValue || undefined;
    const metric = this.metricsValue[this.metricKey];
    const money = metric ? metric.currency : true;

    if (!money || !this.currencyValue) {
      return new Intl.NumberFormat(locale, options).format(value);
    }

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: this.currencyValue,
      ...options,
    }).format(value);
  }

  // Mirrors the service's change_percentage: no chip without a comparison,
  // +100% when the previous bin was zero.
  percentChange(previous, current) {
    if (previous === undefined || previous === null) return null;
    if (previous === 0 && current === 0) return null;
    if (previous === 0) return 100;

    return Math.round(((current - previous) / previous) * 100);
  }
}
